"use client";

import type { EventNameType } from "@homeinstead/shared";
import type { AnalyticsBrowser } from "@segment/analytics-next";
import type { EventPayload } from "../types";

import { identifyAnonymous } from "./identify-anonymous";

const ANALYTICS_EVENT_QUEUE_MAX_LENGTH = 50;

type QueuedAnalyticsEvent =
  | { properties?: Record<string, unknown>; type: "page" }
  | {
      eventName: EventNameType;
      properties?: EventPayload;
      transport?: "sendBeacon";
      type: "track";
    }
  | { traits?: Record<string, unknown>; type: "identify" };

const eventQueue: QueuedAnalyticsEvent[] = [];

let analyticsReadyForEvents = false;
let analyticsInitFailed = false;

function isAnalyticsReadyForEvents(): boolean {
  return analyticsReadyForEvents;
}

function markAnalyticsReadyForEvents(): void {
  analyticsReadyForEvents = true;
}

function markAnalyticsInitFailed(reason: string): void {
  if (analyticsInitFailed) {
    return;
  }

  analyticsInitFailed = true;

  if (eventQueue.length > 0) {
    console.warn(
      `Analytics init failed (${reason}); discarding ${eventQueue.length} queued event(s).`,
    );
    eventQueue.length = 0;
  }
}

// Source middleware reads window.location when Segment accepts the event.
// Queued events are accepted at flush, which can be a later page, so the
// location is captured when the event is recorded. Caller properties win.
function withPageLocationAtEnqueue(
  properties: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (typeof window === "undefined") {
    return properties;
  }

  return {
    page_path: window.location.pathname,
    page_url: window.location.href,
    ...properties,
  };
}

function pushQueuedEvent(event: QueuedAnalyticsEvent): void {
  if (analyticsInitFailed) {
    return;
  }

  if (eventQueue.length >= ANALYTICS_EVENT_QUEUE_MAX_LENGTH) {
    console.warn(
      "Analytics event queue is full; dropping the oldest queued event.",
    );
    eventQueue.shift();
  }

  eventQueue.push(event);
}

async function dispatchQueuedEvent(
  analytics: AnalyticsBrowser,
  event: QueuedAnalyticsEvent,
): Promise<void> {
  if (event.type === "track") {
    analytics.track(
      event.eventName,
      event.properties,
      event.transport ? { transport: event.transport } : undefined,
    );
    return;
  }

  if (event.type === "page") {
    analytics.page(event.properties);
    return;
  }

  if (event.type === "identify") {
    await identifyAnonymous(event.traits, analytics);
  }
}

function enqueuePage(
  analytics: AnalyticsBrowser,
  properties?: Record<string, unknown>,
): void {
  if (isAnalyticsReadyForEvents()) {
    analytics.page(properties);
    return;
  }

  pushQueuedEvent({
    properties: withPageLocationAtEnqueue(properties),
    type: "page",
  });
}

function enqueueTrack(
  analytics: AnalyticsBrowser,
  eventName: EventNameType,
  properties?: EventPayload,
  transport?: "sendBeacon",
): void {
  if (isAnalyticsReadyForEvents()) {
    analytics.track(
      eventName,
      properties,
      transport ? { transport } : undefined,
    );
    return;
  }

  pushQueuedEvent({
    eventName,
    properties: withPageLocationAtEnqueue(properties),
    transport,
    type: "track",
  });
}

function enqueueIdentify(
  analytics: AnalyticsBrowser,
  traits?: Record<string, unknown>,
): void {
  if (isAnalyticsReadyForEvents()) {
    void identifyAnonymous(traits, analytics);
    return;
  }

  pushQueuedEvent({ traits, type: "identify" });
}

async function flushAnalyticsEventQueue(
  analytics: AnalyticsBrowser,
): Promise<void> {
  const queuedEvents = eventQueue.splice(0, eventQueue.length);

  for (const event of queuedEvents) {
    await dispatchQueuedEvent(analytics, event);
  }
}

export {
  enqueueIdentify,
  enqueuePage,
  enqueueTrack,
  flushAnalyticsEventQueue,
  isAnalyticsReadyForEvents,
  markAnalyticsInitFailed,
  markAnalyticsReadyForEvents,
};
